/**
 * LearningPickerModal Component
 * Modal for browsing and selecting learning resources
 */

import React, { useState, useMemo } from "react";
import { useGetLearningResourcesQuery } from "../../../services/api";

// Resource type options
const RESOURCE_TYPES = [
  { value: "", label: "All Types" },
  { value: "video", label: "Video" },
  { value: "article", label: "Article" },
  { value: "podcast", label: "Podcast" },
  { value: "book", label: "Book" },
  { value: "course", label: "Course" },
  { value: "webinar", label: "Webinar" },
  { value: "tool", label: "Tool" },
  { value: "template", label: "Template" },
];

// Type badge colors
const TYPE_COLORS = {
  video: { bg: "#dbeafe", text: "#1e40af" },
  article: { bg: "#d1fae5", text: "#065f46" },
  podcast: { bg: "#fce7f3", text: "#9d174d" },
  book: { bg: "#fef3c7", text: "#92400e" },
  course: { bg: "#e0e7ff", text: "#3730a3" },
  webinar: { bg: "#f3e8ff", text: "#7c3aed" },
  tool: { bg: "#fed7d7", text: "#c53030" },
  template: { bg: "#e5e7eb", text: "#374151" },
};

export default function LearningPickerModal({ onSelect, onClose }) {
  // API hook - limit max is 100 per backend validation
  const { data, isLoading, error } = useGetLearningResourcesQuery({ limit: 100 });
  const resources = data?.resources || [];

  // Local state
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("");

  // Filter resources
  const filteredResources = useMemo(() => {
    return resources.filter((resource) => {
      const matchesSearch =
        !searchQuery ||
        resource.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        resource.description?.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesType = !typeFilter || resource.type === typeFilter;

      return matchesSearch && matchesType;
    });
  }, [resources, searchQuery, typeFilter]);

  // Handle selection
  const handleSelect = (resource) => {
    onSelect({
      _id: resource._id,
      title: resource.title,
      url: resource.url,
      type: resource.type,
      description: resource.description,
    });
  };

  const getTypeColors = (type) => {
    return TYPE_COLORS[type] || TYPE_COLORS.template;
  };

  return (
    <div className="cd-modal-overlay" onClick={onClose}>
      <div className="lp-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="cd-modal-header">
          <h2 className="cd-modal-title">Select Learning Resource</h2>
          <button className="cd-modal-close" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="lp-modal-body">
          {/* Filters */}
          <div className="lp-filters">
            <input
              type="text"
              className="cd-input lp-search"
              placeholder="Search resources..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <select
              className="cd-input lp-type-filter"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
            >
              {RESOURCE_TYPES.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          {/* Resource List */}
          {isLoading ? (
            <div className="lp-loading">Loading resources...</div>
          ) : error ? (
            <div className="lp-empty lp-error">
              Error loading resources: {error.data?.message || error.message || 'Unknown error'}
            </div>
          ) : filteredResources.length === 0 ? (
            <div className="lp-empty">
              {searchQuery || typeFilter
                ? "No resources match your filters"
                : "No learning resources available. Add resources in the Learning Library section first."}
            </div>
          ) : (
            <div className="lp-resource-list">
              {filteredResources.map((resource) => {
                const colors = getTypeColors(resource.type);
                return (
                  <div
                    key={resource._id}
                    className="lp-resource-item"
                    onClick={() => handleSelect(resource)}
                  >
                    <span
                      className="lp-resource-type"
                      style={{ backgroundColor: colors.bg, color: colors.text }}
                    >
                      {resource.type}
                    </span>
                    <div className="lp-resource-info">
                      <span className="lp-resource-title">{resource.title}</span>
                      {resource.description && (
                        <span className="lp-resource-desc">
                          {resource.description.length > 100
                            ? resource.description.substring(0, 100) + "..."
                            : resource.description}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Results count */}
          <div className="lp-count">
            {filteredResources.length} resource{filteredResources.length !== 1 ? "s" : ""} found
          </div>
        </div>

        <div className="cd-modal-footer">
          <button className="cd-btn cd-btn-secondary" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>

      <style>{`
        .lp-modal-content {
          background: #fff;
          border-radius: 12px;
          max-width: 600px;
          width: 95%;
          max-height: 80vh;
          display: flex;
          flex-direction: column;
        }

        .lp-modal-body {
          padding: 0 24px 24px;
          overflow-y: auto;
          flex: 1;
        }

        .lp-filters {
          display: flex;
          gap: 12px;
          margin-bottom: 16px;
        }

        .lp-search {
          flex: 1;
        }

        .lp-type-filter {
          width: 140px;
        }

        .lp-loading,
        .lp-empty {
          text-align: center;
          padding: 32px;
          color: #6b7280;
          font-size: 14px;
        }

        .lp-error {
          color: #dc2626;
          background: #fef2f2;
          border-radius: 8px;
        }

        .lp-resource-list {
          max-height: 400px;
          overflow-y: auto;
        }

        .lp-resource-item {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 12px;
          border-radius: 8px;
          cursor: pointer;
          transition: background 0.15s ease;
          border: 1px solid transparent;
        }

        .lp-resource-item:hover {
          background: #f3f4f6;
          border-color: #2A987A;
        }

        .lp-resource-type {
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 10px;
          font-weight: 500;
          text-transform: uppercase;
          white-space: nowrap;
          flex-shrink: 0;
        }

        .lp-resource-info {
          flex: 1;
          min-width: 0;
        }

        .lp-resource-title {
          display: block;
          font-size: 14px;
          font-weight: 500;
          color: #111827;
          margin-bottom: 4px;
        }

        .lp-resource-desc {
          display: block;
          font-size: 12px;
          color: #6b7280;
          line-height: 1.4;
        }

        .lp-count {
          margin-top: 12px;
          padding-top: 12px;
          border-top: 1px solid #e5e7eb;
          font-size: 13px;
          color: #6b7280;
        }
      `}</style>
    </div>
  );
}
